import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { api, setUnauthorizedHandler, tokenStorage } from '../api/client';
import type { CurrentUser, Permission, SetupState } from '../api/types';
import { resetPluginTranslations } from '../i18n';
import { AuthContext, type AuthState } from './AuthContext';

/// Holds the session for the whole app: whether this server has been set up at all, who is
/// signed in, and what they may do. Everything else asks this rather than the server.
export default function AuthProvider({ children }: { children: ReactNode }) {
  const [setup, setSetup] = useState<SetupState>();
  const [user, setUser] = useState<CurrentUser>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();

  const load = useCallback(async () => {
    try {
      const state = await api.setupState();
      setSetup(state);
      setError(undefined);

      if (!state.isComplete) {
        // Nothing to be signed in as yet; the wizard takes over.
        setUser(undefined);
        return;
      }

      if (tokenStorage.read()) {
        setUser(await api.currentUser());
      } else {
        setUser(undefined);
      }
    } catch (caught) {
      // A token the server no longer accepts is not an error worth showing: it just means
      // the sign-in screen.
      setUser(undefined);
      setError(caught instanceof Error ? caught : new Error(String(caught)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => setUser(undefined));

    // Reading the session once on mount is exactly what an effect is for. The lint rule cannot
    // tell this from a render-triggered state update, because it does not follow the await.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const value = useMemo<AuthState>(
    () => ({
      setup,
      user,
      loading,
      error,
      signIn: async (userName, password) => {
        const result = await api.signIn({ userName, password, clientName: 'Emiro Web' });
        tokenStorage.write(result.token);
        setUser(result.user);
        setError(undefined);
      },
      signOut: async () => {
        try {
          await api.signOut();
        } finally {
          tokenStorage.write(null);
          setUser(undefined);

          // The next person to sign in may be looking at a server whose plugins have changed,
          // so their catalogues are fetched again rather than reused.
          resetPluginTranslations();
        }
      },
      adopt: (token, signedIn) => {
        tokenStorage.write(token);
        setUser(signedIn);
      },
      refresh: load,
      can: (permission: Permission) => user?.permissions.includes(permission) ?? false,
    }),
    [setup, user, loading, error, load],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
